// src/sync/projectStoreSyncProvider.ts

/**
 * Project Store Sync Provider - the real local side of project-level sync.
 *
 * Implements SyncLocalProvider by reading the live project store plus the
 * canonical schema, and applying an incoming cloud record through the store's
 * own restoreFromCloud entry point (so the engine never reaches into store
 * internals).
 *
 * Last-modified semantics: a stable per-project timestamp that advances ONLY
 * when the canonical fingerprint actually changes. This keeps last-writer-wins
 * meaningful (the local side does not look "newer" on every read) and is the
 * basis for the engine's push-vs-pull decision.
 */

import { useProjectStore } from "@/store/projectStore";
import { getCanonicalSchema } from "@/core/schema/canonicalProvider";
import { persistSave, persistLoad } from "@/persistence/persistenceEngine";
import type { CloudProjectRecord, SyncLocalProvider } from "@/sync/cloudSyncEngine";

const DEVICE_ID_KEY = "nuvra-device-id";

interface LocalSyncMeta { fingerprint: string; modifiedAt: number }

function metaKey(projectId: string): string {
  return `sync-local-meta-${projectId}`;
}

function deviceId(): string {
  if (typeof localStorage === "undefined") return "local";
  return localStorage.getItem(DEVICE_ID_KEY) ?? "local";
}

/**
 * Resolve a stable lastModifiedAt for the current content. Advances only when
 * the fingerprint changed since the last read, so repeated reads of unchanged
 * content return the same timestamp.
 */
function resolveModifiedAt(projectId: string, fingerprint: string): number {
  const prev = persistLoad<LocalSyncMeta>(metaKey(projectId));
  if (prev && prev.fingerprint === fingerprint) return prev.modifiedAt;
  const modifiedAt = Date.now();
  persistSave(metaKey(projectId), { fingerprint, modifiedAt });
  return modifiedAt;
}

export function createProjectStoreSyncProvider(): SyncLocalProvider {
  return {
    getRecord(): CloudProjectRecord | null {
      const store = useProjectStore.getState();
      const canonical = getCanonicalSchema();
      if (!store.projectId || !canonical) return null;

      const fingerprint = canonical.fingerprint;

      return {
        projectId:         store.projectId,
        schemaFingerprint: fingerprint,
        schemaVersion:     canonical.version,
        lastModifiedAt:    resolveModifiedAt(store.projectId, fingerprint),
        lastModifiedBy:    deviceId(),
        deviceId:          deviceId(),
        data: {
          // Restore-faithful store projections (count fields drive conflict detection).
          pages:      store.pages,
          models:     store.schema,
          components: canonical.components,
          actions:    canonical.actions,
          design:     store.designSystem,
          workflows:  store.workflows,
          settings:   store.settings,
          // Everything needed to reconstitute the store exactly on apply.
          meta: {
            canonicalSchema: canonical,
            blocks:          store.blocks,
            files:           store.files,
          },
        },
      };
    },

    applyRecord(record: CloudProjectRecord): void {
      const meta = (record.data.meta ?? {}) as {
        canonicalSchema?: unknown;
        blocks?: unknown;
        files?: unknown;
      };

      useProjectStore.getState().restoreFromCloud({
        projectId:       record.projectId,
        pages:           record.data.pages,
        schema:          record.data.models,
        workflows:       record.data.workflows,
        designSystem:    record.data.design,
        settings:        record.data.settings,
        blocks:          meta.blocks,
        files:           meta.files,
        canonicalSchema: meta.canonicalSchema,
      });

      // Mark local as in-sync with the applied content so the next read does not
      // spuriously advance lastModifiedAt past the cloud's stamp.
      persistSave(metaKey(record.projectId), {
        fingerprint: record.schemaFingerprint,
        modifiedAt:  record.lastModifiedAt,
      });
    },
  };
}
